/**
 * StudentDetail Component
 * Modal displaying detailed student information
 */

import React from 'react';
import type { StudentDetail as StudentDetailType } from '@shared/types/teach';
import { X, User, Award, CreditCard, BookOpen, Calendar } from 'lucide-react';

interface StudentDetailProps {
  student: StudentDetailType;
  onClose: () => void;
}

export const StudentDetail: React.FC<StudentDetailProps> = ({ student, onClose }) => {
  const getMembershipLabel = (type: string) => {
    const labels: Record<string, string> = {
      creditPay: '按次付费',
      monthly: '月会员',
      yearly: '年会员',
    };
    return labels[type] || type;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return `¥${(amount / 100).toFixed(2)}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            学生详情
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-4">
              <User className="w-5 h-5 text-blue-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                基本信息
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400">昵称</label>
                <p className="text-gray-900 dark:text-white font-medium">{student.nickname}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400">年级</label>
                <p className="text-gray-900 dark:text-white font-medium">{student.grade}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400">手机号</label>
                <p className="text-gray-900 dark:text-white font-medium">{student.phone}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400">注册时间</label>
                <p className="text-gray-900 dark:text-white font-medium">{formatDate(student.createdAt)}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400">会员类型</label>
                <p className="text-gray-900 dark:text-white font-medium">
                  {getMembershipLabel(student.membershipType)}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400">会员截止</label>
                <p className="text-gray-900 dark:text-white font-medium">
                  {student.membershipExpiry ? formatDate(student.membershipExpiry) : '无'}
                </p>
              </div>
            </div>
          </div>

          {/* Learning Statistics */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-4">
              <Award className="w-5 h-5 text-green-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                学习统计
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">完成话题数</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {student.stats.completedTopics}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">学习币余额</p>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {student.stats.creditBalance}
                </p>
              </div>
            </div>

            {/* Recent Activities */}
            {student.stats.recentActivities.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  最近学习记录
                </h4>
                <div className="space-y-2">
                  {student.stats.recentActivities.map((activity) => (
                    <div
                      key={activity.topicId}
                      className="bg-white dark:bg-gray-800 rounded-lg p-3 flex items-center justify-between"
                    >
                      <div className="flex items-center space-x-2">
                        <BookOpen className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900 dark:text-white">
                          {activity.title}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(activity.completedAt)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Payment History */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-4">
              <CreditCard className="w-5 h-5 text-yellow-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                付费历史
              </h3>
            </div>
            {student.payments.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                暂无付费记录
              </p>
            ) : (
              <div className="space-y-2">
                {student.payments.map((payment) => (
                  <div
                    key={payment.id}
                    className="bg-white dark:bg-gray-800 rounded-lg p-3 flex items-center justify-between"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {payment.productName}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(payment.createdAt)}
                      </p>
                    </div>
                    <span className="text-sm font-bold text-green-600 dark:text-green-400">
                      {formatCurrency(payment.amount)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Classes */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-4">
              <Calendar className="w-5 h-5 text-purple-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                所在班级
              </h3>
            </div>
            {student.classes.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                未加入任何班级
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {student.classes.map((cls) => (
                  <div
                    key={cls.id}
                    className="bg-white dark:bg-gray-800 rounded-lg p-3"
                  >
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {cls.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      加入于 {formatDate(cls.joinedAt)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
};
